import { CloseOutlined, ExpandMoreOutlined, SearchOutlined } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getSysProducts } from 'src/shared/functions/getSysProducts';
import { MODULE_LABELS, WIDGET_CATALOG, WIDGET_ICONS } from './widgetCatalog';
import { WidgetDefinition, WidgetModule } from './types';

type WidgetLibraryProps = {
  open: boolean;
  onClose: () => void;
  onAdd: (widget: WidgetDefinition) => void;
  usedWidgetIds: Set<string>;
};

export const WidgetLibrary = ({ open, onClose, onAdd, usedWidgetIds }: WidgetLibraryProps) => {
  const [search, setSearch] = useState('');
  const { currentUser } = useGlobals();

  const availableModules = useMemo<Set<WidgetModule>>(() => {
    const sysProducts = getSysProducts(currentUser);
    const yvNames = new Set(sysProducts.map((p) => p.yvName.toLowerCase()));
    const modules = new Set<WidgetModule>();
    (['memberReg', 'invoice', 'fundraiser', 'event'] as WidgetModule[]).forEach((m) => {
      if (yvNames.has(m.toLowerCase())) modules.add(m);
    });
    return modules;
  }, [currentUser]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return WIDGET_CATALOG.filter((w) => {
      if (!availableModules.has(w.module)) return false;
      if (!query) return true;
      return w.name.toLowerCase().includes(query) || w.description.toLowerCase().includes(query);
    });
  }, [search, availableModules]);

  const groups = useMemo(() => {
    const byModule = new Map<WidgetModule, WidgetDefinition[]>();
    filtered.forEach((w) => {
      const arr = byModule.get(w.module) ?? [];
      arr.push(w);
      byModule.set(w.module, arr);
    });
    return Array.from(byModule.entries());
  }, [filtered]);

  const [expanded, setExpanded] = useState<string | false>('memberReg');

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: 360,
          border: 'none',
          borderLeft: '1px solid #dadde1',
          borderRadius: 0,
          boxShadow: 'none',
        },
      }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ p: 2, borderBottom: '1px solid #f1f5f9' }}>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Widget-bibliotek</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Klicka för att lägga till</Typography>
          </Box>
          <IconButton size='small' onClick={onClose}>
            <CloseOutlined fontSize='small' />
          </IconButton>
        </Stack>
        <Box sx={{ p: 2, borderBottom: '1px solid #f1f5f9' }}>
          <TextField
            size='small'
            fullWidth
            placeholder='Sök widget…'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined fontSize='small' />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          {groups.map(([moduleId, widgets]) => (
            <Accordion
              key={moduleId}
              elevation={0}
              expanded={search.trim() !== '' ? true : expanded === moduleId}
              onChange={(_, isExpanded) => setExpanded(isExpanded ? moduleId : false)}
              sx={{ '&:before': { display: 'none' }, border: '1px solid #f1f5f9', borderRadius: 1, mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreOutlined fontSize='small' />}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {MODULE_LABELS[moduleId] ?? moduleId}
                  <Typography component='span' sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 400, ml: 1 }}>
                    {widgets.length}
                  </Typography>
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 1, pb: 1 }}>
                <List disablePadding sx={{ '& .MuiListItemButton-root': { borderRadius: 1, mb: 0.5 } }}>
                  {widgets.map((w) => {
                    const inUse = usedWidgetIds.has(w.id);
                    return (
                      <ListItemButton
                        key={w.id}
                        onClick={() => onAdd(w)}
                        sx={{
                          border: '1px solid #f1f5f9',
                          alignItems: 'flex-start',
                          ...(inUse && { bgcolor: '#f8fafc', '&:hover': { bgcolor: '#f1f5f9' } }),
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.5, color: 'text.secondary' }}>
                          {WIDGET_ICONS[w.iconName] ?? WIDGET_ICONS.Default}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Stack direction='row' spacing={1} alignItems='center'>
                              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{w.name}</Typography>
                              {inUse && (
                                <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#2e7d32', bgcolor: '#e8f5e9', px: 0.75, borderRadius: 0.5 }}>
                                  Tillagd
                                </Typography>
                              )}
                              {w.configurable && (
                                <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#2c5077', bgcolor: '#e3edf7', px: 0.75, borderRadius: 0.5 }}>
                                  Konfig
                                </Typography>
                              )}
                            </Stack>
                          }
                          secondary={
                            <Stack spacing={0.25}>
                              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{w.description}</Typography>
                              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                                {w.defaultW}×{w.defaultH} celler
                              </Typography>
                            </Stack>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
          {filtered.length === 0 && (
            <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center', mt: 4 }}>
              Inga widgets matchar &quot;{search}&quot;
            </Typography>
          )}
        </Box>
      </Stack>
    </Drawer>
  );
};
